import { useEffect, useRef } from "react";

type SectionRevealOptions = {
  targetSelector?: string;
  threshold?: number;
  rootMargin?: string;
};

export function useSectionReveal({
  targetSelector = ".fade-in-section",
  threshold = 0.1,
  rootMargin = "0px 0px -50px 0px",
}: SectionRevealOptions = {}) {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold, rootMargin },
    );

    const elements = document.querySelectorAll(targetSelector);
    elements.forEach((el) => observerRef.current?.observe(el));

    // Immediately reveal elements already in view on first paint
    const revealInitiallyVisible = () => {
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const inView =
          rect.top < window.innerHeight * (1 - threshold) && rect.bottom > 0;
        if (inView) {
          el.classList.add("animate-in");
        }
      });
    };
    revealInitiallyVisible();

    return () => {
      observerRef.current?.disconnect();
    };
  }, [targetSelector, threshold, rootMargin]);
}
